"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Money = exports.MoneyCurrencyMismatchError = exports.MoneyError = exports.LEDGER_DECIMAL_PLACES = void 0;
const decimal_js_1 = __importDefault(require("decimal.js"));
const currency_1 = require("./currency");
exports.LEDGER_DECIMAL_PLACES = 3;
class MoneyError extends Error {
    constructor(message) {
        super(message);
        this.name = 'MoneyError';
    }
}
exports.MoneyError = MoneyError;
class MoneyCurrencyMismatchError extends MoneyError {
    expected;
    received;
    constructor(expected, received) {
        super(`Moeda incompatível: esperada ${expected}, recebida ${received}`);
        this.expected = expected;
        this.received = received;
        this.name = 'MoneyCurrencyMismatchError';
    }
}
exports.MoneyCurrencyMismatchError = MoneyCurrencyMismatchError;
class Money {
    amount;
    currency;
    constructor(amount, currency) {
        this.amount = amount;
        this.currency = currency;
    }
    static of(value, currency) {
        return new Money(new decimal_js_1.default(value), currency);
    }
    static zero(currency) {
        return new Money(new decimal_js_1.default(0), currency);
    }
    static fromMinorUnits(minor, currency) {
        const digits = (0, currency_1.minorUnitDigits)(currency);
        return new Money(new decimal_js_1.default(minor.toString()).div(new decimal_js_1.default(10).pow(digits)), currency);
    }
    static sum(values, currency) {
        return values.reduce((acc, m) => acc.plus(m), Money.zero(currency));
    }
    assertSameCurrency(other) {
        if (other.currency !== this.currency) {
            throw new MoneyCurrencyMismatchError(this.currency, other.currency);
        }
    }
    plus(other) {
        this.assertSameCurrency(other);
        return new Money(this.amount.plus(other.amount), this.currency);
    }
    minus(other) {
        this.assertSameCurrency(other);
        return new Money(this.amount.minus(other.amount), this.currency);
    }
    times(factor) {
        return new Money(this.amount.times(factor), this.currency);
    }
    percent(pct) {
        return new Money(this.amount.times(new decimal_js_1.default(pct).div(100)), this.currency);
    }
    round(decimalPlaces = exports.LEDGER_DECIMAL_PLACES) {
        return new Money(this.amount.toDecimalPlaces(decimalPlaces, decimal_js_1.default.ROUND_HALF_UP), this.currency);
    }
    /** Final financial amounts use the currency unit; rates and intermediate math keep precision. */
    roundToCurrency() {
        return this.round((0, currency_1.minorUnitDigits)(this.currency));
    }
    /**
     * Cumulative, house-monotone allocation of a refund across finalized amounts.
     * Highest averages starts at the lower quotas and assigns fewer than N remaining
     * units. Integer cross-products avoid rounding in priority comparisons. Ties use
     * the persisted order. A later cumulative refund never reduces an earlier share.
     */
    allocateCumulative(parts) {
        const target = this.toMinorUnits();
        const weights = parts.map((part) => {
            this.assertSameCurrency(part);
            if (!part.equals(part.roundToCurrency()) || part.isNegative()) {
                throw new MoneyError('rateio acumulado exige parcelas finais nao negativas');
            }
            return part.toMinorUnits();
        });
        const total = weights.reduce((sum, weight) => sum + weight, 0n);
        if (!this.equals(this.roundToCurrency()) || target < 0n || target > total) {
            throw new MoneyError('valor acumulado fora do total das parcelas');
        }
        if (target === 0n)
            return parts.map(() => Money.zero(this.currency));
        const allocated = weights.map((weight) => (target * weight) / total);
        let remaining = target - allocated.reduce((sum, amount) => sum + amount, 0n);
        while (remaining > 0n) {
            let best = -1;
            for (let index = 0; index < weights.length; index++) {
                if (weights[index] === 0n)
                    continue;
                if (best < 0 || weights[index] * (allocated[best] + 1n) > weights[best] * (allocated[index] + 1n))
                    best = index;
            }
            if (best < 0)
                throw new MoneyError('rateio acumulado sem destinatario');
            allocated[best] = allocated[best] + 1n;
            remaining--;
        }
        return allocated.map((amount) => Money.fromMinorUnits(amount, this.currency));
    }
    abs() {
        return new Money(this.amount.abs(), this.currency);
    }
    isZero() {
        return this.amount.isZero();
    }
    isNegative() {
        return this.amount.isNegative();
    }
    isPositive() {
        return this.amount.greaterThan(0);
    }
    equals(other) {
        this.assertSameCurrency(other);
        return this.amount.equals(other.amount);
    }
    greaterThan(other) {
        this.assertSameCurrency(other);
        return this.amount.greaterThan(other.amount);
    }
    greaterThanOrEqual(other) {
        this.assertSameCurrency(other);
        return this.amount.greaterThanOrEqualTo(other.amount);
    }
    lessThan(other) {
        this.assertSameCurrency(other);
        return this.amount.lessThan(other.amount);
    }
    lessThanOrEqual(other) {
        this.assertSameCurrency(other);
        return this.amount.lessThanOrEqualTo(other.amount);
    }
    toMinorUnits() {
        const digits = (0, currency_1.minorUnitDigits)(this.currency);
        const scaled = this.amount
            .times(new decimal_js_1.default(10).pow(digits))
            .toDecimalPlaces(0, decimal_js_1.default.ROUND_HALF_UP);
        return BigInt(scaled.toFixed(0));
    }
    toFixed(decimalPlaces = exports.LEDGER_DECIMAL_PLACES) {
        return this.amount.toDecimalPlaces(decimalPlaces, decimal_js_1.default.ROUND_HALF_UP).toFixed(decimalPlaces);
    }
    toDecimal() {
        return this.amount;
    }
    toString() {
        return `${this.amount.toFixed()} ${this.currency}`;
    }
    splitByWeights(weights, decimalPlaces = exports.LEDGER_DECIMAL_PLACES) {
        if (weights.length === 0) {
            throw new MoneyError('splitByWeights exige ao menos um peso');
        }
        const total = weights.reduce((acc, w) => acc.plus(new decimal_js_1.default(w)), new decimal_js_1.default(0));
        if (total.isZero() || total.isNegative()) {
            throw new MoneyError('a soma dos pesos deve ser positiva');
        }
        const whole = this.amount.toDecimalPlaces(decimalPlaces, decimal_js_1.default.ROUND_HALF_UP);
        const parts = [];
        let allocated = new decimal_js_1.default(0);
        for (let i = 0; i < weights.length; i++) {
            const isLast = i === weights.length - 1;
            if (isLast) {
                parts.push(whole.minus(allocated));
            }
            else {
                const share = whole
                    .times(new decimal_js_1.default(weights[i]))
                    .div(total)
                    .toDecimalPlaces(decimalPlaces, decimal_js_1.default.ROUND_HALF_UP);
                parts.push(share);
                allocated = allocated.plus(share);
            }
        }
        return parts.map((p) => new Money(p, this.currency));
    }
    splitByWeightsLargestRemainder(weights, decimalPlaces = exports.LEDGER_DECIMAL_PLACES) {
        if (weights.length === 0) {
            throw new MoneyError('splitByWeightsLargestRemainder exige ao menos um peso');
        }
        const normalizedWeights = weights.map((weight) => new decimal_js_1.default(weight));
        if (normalizedWeights.some((weight) => weight.isNegative())) {
            throw new MoneyError('pesos negativos nao sao permitidos');
        }
        const totalWeight = normalizedWeights.reduce((acc, weight) => acc.plus(weight), new decimal_js_1.default(0));
        if (!totalWeight.isPositive()) {
            throw new MoneyError('a soma dos pesos deve ser positiva');
        }
        const scale = new decimal_js_1.default(10).pow(decimalPlaces);
        const signedUnits = this.amount.times(scale).toDecimalPlaces(0, decimal_js_1.default.ROUND_HALF_UP);
        const sign = signedUnits.isNegative() ? new decimal_js_1.default(-1) : new decimal_js_1.default(1);
        const absoluteUnits = signedUnits.abs();
        const rawShares = normalizedWeights.map((weight) => absoluteUnits.times(weight).div(totalWeight));
        const allocatedUnits = rawShares.map((share) => share.floor());
        const allocatedTotal = allocatedUnits.reduce((acc, units) => acc.plus(units), new decimal_js_1.default(0));
        const remainingUnits = absoluteUnits.minus(allocatedTotal).toNumber();
        const remainderOrder = rawShares
            .map((share, index) => ({ index, remainder: share.minus(allocatedUnits[index]) }))
            .sort((a, b) => {
            const remainder = b.remainder.comparedTo(a.remainder);
            return remainder !== 0 ? remainder : a.index - b.index;
        });
        for (let index = 0; index < remainingUnits; index++) {
            const target = remainderOrder[index];
            if (target === undefined) {
                throw new MoneyError('residuo maior que a quantidade de pesos');
            }
            allocatedUnits[target.index] = allocatedUnits[target.index].plus(1);
        }
        return allocatedUnits.map((units) => new Money(units.times(sign).div(scale), this.currency));
    }
}
exports.Money = Money;
//# sourceMappingURL=money.js.map